import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { useProductComboOption } from "@react/hooks/useProductComboOption";
import { useProductSelector } from "@react/sections/ProductHero/ProductSelector/ProductSelectorContext";
import {
  getIntelligemsComparePriceByVariantId,
  getIntelligemsPriceByVariantId,
  getShopifyGlobalId,
  updateUrlVariantId,
} from "@root/tablescape/utilities";
import {
  TablescapeContext,
  TablescapeEvent,
} from "@root/tablescape/utilities/tablescapeContext";
import { type ProductComboOption as CurrentProductComboOption } from "@sections/ProductHero/ProductSelector/ProductSelector.view";
import { type CartProductVariant, ThemeMode } from "@tablescape/state";

import { ProductComboOptionView } from "./ProductComboOption.view";
import { ProductComboOptionPlaceholder } from "./ProductComboOptionPlaceholder.view";

type Props = {
  index: number;
  isActive: boolean;
  productComboOptionId: string;
  selectedVariants: CartProductVariant[];
  theme: ThemeMode;
};

const ProductComboOptionBase = ({
  index,
  isActive,
  productComboOptionId,
  selectedVariants,
  theme,
}: Props) => {
  const {
    setSelectedIndex,
    setSelectedProductComboOption,
    setSelectedVariantsByIndex,
  } = useProductSelector();
  const {
    badgeText,
    badgeType,
    description,
    isCurrentPdpProduct,
    isLoading,
    isMigratedBundle,
    isProductFixedBundle,
    isVariantFixedBundle,
    originalProducts,
    products,
    showThumbnail,
    title,
  } = useProductComboOption(productComboOptionId);

  const resolvedBadgeType = useMemo(
    () => badgeType || (theme === ThemeMode.LIGHT ? "primary" : "secondary"),
    [badgeType, theme]
  );

  const localTheme = useMemo(
    () => (isActive ? ThemeMode.LIGHT : theme),
    [isActive, theme]
  );

  const [currentProductComboOption, setCurrentProductComboOption] =
    useState<CurrentProductComboOption>({});

  const setSelectedVariants = useCallback(
    (variants: CartProductVariant[]) => {
      setSelectedVariantsByIndex({
        [index]: variants,
      });
    },
    [index, setSelectedVariantsByIndex]
  );

  useEffect(() => {
    if (products.length !== selectedVariants.length) {
      const matchedVariants = products.map((product) => {
        if (isCurrentPdpProduct) {
          const queryParams = new URLSearchParams(window.location.search);
          const selectedVariantId = queryParams.get("variant");
          if (selectedVariantId) {
            const matchedVariant = product.variants.find(
              (variant) =>
                variant.id ===
                getShopifyGlobalId("ProductVariant", selectedVariantId)
            );

            if (matchedVariant) return matchedVariant;
          }
        }

        return (
          product.variants.find(
            (variant) => variant.availableForSale && !variant.hidden
          ) ?? product.variants[0]
        );
      });

      setSelectedVariants(matchedVariants);
    }
  }, [
    products,
    selectedVariants.length,
    setSelectedVariants,
    isCurrentPdpProduct,
  ]);

  const handleProductComboOptionClick = useCallback(
    () => setSelectedIndex(index),
    [index, setSelectedIndex]
  );

  const handleSelectedVariantId = useCallback(
    (variantId: CartProductVariant["id"]) => {
      const updatedSelectedVariants = products.map((product, index) => {
        const matchedVariant = product.variants.find(
          (variant) => variant.id === variantId
        );

        if (matchedVariant && isCurrentPdpProduct) {
          matchedVariant.selectedOptions.forEach((option) => {
            const input = document.querySelector<HTMLInputElement>(
              `[data-product-option-name="${option.name}"][data-product-variant-title="${option.value}"] input:not(:checked)`
            );

            if (input) {
              input.checked = true;

              const event = new Event("change", {
                bubbles: true,
                cancelable: true,
              });

              input.dispatchEvent(event);
            }
          });

          updateUrlVariantId(matchedVariant.id);
          TablescapeContext.emit(
            TablescapeEvent.PRODUCT_SELECTOR_VARIANT_SELECTED,
            {
              productSelector: { selectedVariantId: matchedVariant.id },
            },
            false
          );
        }

        return matchedVariant ?? selectedVariants[index];
      });

      setSelectedVariants(updatedSelectedVariants);
    },
    [products, setSelectedVariants, selectedVariants, isCurrentPdpProduct]
  );

  const price = useMemo(() => {
    const sumPrice = selectedVariants.reduce((acc, variant) => {
      const intelligemsPrice = getIntelligemsPriceByVariantId(variant.id);
      if (intelligemsPrice) {
        return acc + intelligemsPrice;
      }
      return acc + Number(variant.price.amount);
    }, 0);

    // If product fixed bundle, get price from product fixed bundle variant instead of its components
    if (
      !isMigratedBundle &&
      isProductFixedBundle &&
      originalProducts[0]?.variants[0]?.price
    ) {
      const intelligemsPrice = getIntelligemsPriceByVariantId(
        originalProducts[0].variants[0].id
      );

      const productFixedBundlePriceAmount =
        intelligemsPrice ?? originalProducts[0].variants[0].price.amount;

      if (Number(productFixedBundlePriceAmount) <= sumPrice) {
        return {
          currencyCode: originalProducts[0].variants[0].price.currencyCode,
          amount: productFixedBundlePriceAmount.toString(),
        };
      }
    }

    return {
      currencyCode: selectedVariants[0]
        ? selectedVariants[0].price.currencyCode
        : window.Shopify.currency.active,
      amount: sumPrice.toString(),
    };
  }, [
    selectedVariants,
    originalProducts,
    isProductFixedBundle,
    isMigratedBundle,
  ]);

  useEffect(() => {
    if (
      isActive &&
      Number(price.amount) > 0 &&
      currentProductComboOption.title
    ) {
      setSelectedProductComboOption(currentProductComboOption);
    }
  }, [
    isActive,
    price,
    currentProductComboOption,
    setSelectedProductComboOption,
  ]);

  const compareAtPrice = useMemo(() => {
    if (
      !isMigratedBundle &&
      isProductFixedBundle &&
      originalProducts[0]?.variants[0]?.compareAtPrice
    ) {
      const intelligemsCompareAtPrice = getIntelligemsComparePriceByVariantId(
        originalProducts[0].variants[0].id
      );

      const productFixedBundleCompareAtPriceAmount =
        intelligemsCompareAtPrice ??
        originalProducts[0].variants[0].compareAtPrice.amount;

      return {
        currencyCode:
          originalProducts[0].variants[0].compareAtPrice.currencyCode,
        amount: productFixedBundleCompareAtPriceAmount.toString(),
      };
    }

    const selectedVariantsCompareAtPriceExists = selectedVariants.some(
      (variant) => {
        return !!variant.compareAtPrice;
      }
    );
    if (selectedVariantsCompareAtPriceExists) {
      const sumCompareAtPrice = selectedVariants.reduce((acc, variant) => {
        const intelligemsCompareAtPrice = getIntelligemsComparePriceByVariantId(
          variant.id
        );

        return (
          acc +
          (intelligemsCompareAtPrice ||
            (variant.compareAtPrice
              ? Number(variant.compareAtPrice.amount)
              : Number(variant.price.amount)))
        );
      }, 0);

      return {
        currencyCode: selectedVariants[0]
          ? selectedVariants[0].price.currencyCode
          : window.Shopify.currency.active,
        amount: sumCompareAtPrice.toString(),
      };
    }
  }, [
    selectedVariants,
    originalProducts,
    isProductFixedBundle,
    isMigratedBundle,
  ]);

  const compareAtPriceDisplay = useMemo(() => {
    if (isMigratedBundle) {
      return selectedVariants[0]?.compareAtPriceDisplay;
    }
  }, [isMigratedBundle, selectedVariants]);

  useEffect(() => {
    setCurrentProductComboOption({
      price,
      products: originalProducts,
      title,
      isBundle:
        isProductFixedBundle || isVariantFixedBundle || isMigratedBundle,
    });
  }, [
    isProductFixedBundle,
    isVariantFixedBundle,
    isMigratedBundle,
    originalProducts,
    price,
    title,
  ]);

  return isLoading ? (
    <ProductComboOptionPlaceholder isActive={isActive} />
  ) : (
    <ProductComboOptionView
      badgeText={badgeText}
      badgeType={resolvedBadgeType}
      compareAtPrice={compareAtPrice}
      compareAtPriceDisplay={compareAtPriceDisplay}
      currentProductComboOption={currentProductComboOption}
      description={description}
      handleProductComboOptionClick={handleProductComboOptionClick}
      isActive={isActive}
      localTheme={localTheme}
      price={price}
      products={products}
      selectedVariants={selectedVariants}
      setSelectedVariant={handleSelectedVariantId}
      showThumbnail={showThumbnail}
      theme={theme}
      title={title}
    />
  );
};

export const ProductComboOption = memo(ProductComboOptionBase);
